import { WorkflowPhase, WorkOrderSortKey } from '@atlas/types'
import { SortDirection } from '@atlas/ui-components'
import { Avatar, Button, Icon, type StatusTone, Table, type TableBodyCell, type TableColumn } from '@atlas/ui-kit'
import { DEADLINE_AT_RISK_DAYS } from '@/config/board'
import type { BoardWorkflow, WorkOrder } from '@/domain/work-order.types'
import { findBoardStatus, isTerminalStatus, statusLabel } from '@/domain/work-order.types'
import { formatDayMonthYear } from '@/lib/format-date'
import { initials } from '@/lib/labels'

const DAY_MS = 86_400_000
const MISSING = '—'

export interface WorkOrderTableProps {
	orders: WorkOrder[]
	/** The page's workflow model: each order's status label, phase and terminal flag come from it. */
	workflows: BoardWorkflow[]
	/** Display names by user id, for the Designer column. */
	userNames: ReadonlyMap<string, string>
	/** The brand label each brand-axis code files under (a product maps to its brand); an unknown code shows upper-cased. */
	brandLabels: ReadonlyMap<string, string>
	sort: WorkOrderSortKey | null
	order: SortDirection
	onSort: (key: WorkOrderSortKey) => void
	/** Opens an order's detail page: a row's own order, or the parent its parent button points at. */
	onOpen: (workOrderId: string) => void
	/** Injectable for deterministic deadline highlighting. */
	now?: number
}

/** Statuses are workflow data, so the pill's tone follows the status's phase. */
const PHASE_TONE: Record<WorkflowPhase, StatusTone> = {
	[WorkflowPhase.INTAKE]: 'neutral',
	[WorkflowPhase.PRODUCTION]: 'accent',
	[WorkflowPhase.VALIDATION]: 'purple',
	[WorkflowPhase.DELIVERY]: 'success',
}

/** `D2 · L1`, or nothing when the order carries neither code. */
function assetTypeOf(wo: WorkOrder): string {
	const parts = [wo.assetTypeCode, wo.complexityLevel].filter(Boolean)
	return parts.length ? parts.join(' · ') : MISSING
}

/** The alphabetically first brand among the order's brand codes, the one the brand sort orders it by. */
function brandOf(wo: WorkOrder, brandLabels: ReadonlyMap<string, string>): string {
	const labels = (wo.tuple?.brand ?? []).map((code) => brandLabels.get(code) ?? code.toUpperCase())
	return labels.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))[0] ?? MISSING
}

/** The order code over its title, with a button to the parent order beside the code when the order has one. */
function IdName({ wo, onOpen }: { wo: WorkOrder; onOpen: (workOrderId: string) => void }) {
	const parentId = wo.parentWorkOrderId
	return (
		<div className="flex min-w-0 flex-1 flex-col gap-0.5">
			<div className="flex min-w-0 items-center gap-1.5">
				<span className="truncate text-atlas-label-medium text-atlas-light-dark">{wo.orderCode}</span>
				{parentId ? (
					<Button
						variant="text-link"
						aria-label={`Open the parent work order of ${wo.orderCode}`}
						className="-my-0.5 gap-1 rounded-atlas-full px-2 py-0.5 text-atlas-label-medium transition-colors data-hovered:bg-atlas-accent-light"
						onPress={() => onOpen(parentId)}
					>
						<Icon name="open" className="size-3.5" />
						Parent
					</Button>
				) : null}
			</div>
			<span className="truncate text-atlas-body text-atlas-dark" title={wo.title}>
				{wo.title}
			</span>
		</div>
	)
}

function designerCell(ids: string[], userNames: ReadonlyMap<string, string>): TableBodyCell {
	if (ids.length === 0) return { type: 'designer' }
	const names = ids.map((id) => userNames.get(id) ?? 'Unknown user')
	return (
		<div className="flex -space-x-1.5" title={names.join(', ')}>
			{names.map((name, index) => (
				<Avatar key={ids[index]} size="sm" tint="lila" initials={initials(name)} name={name} />
			))}
		</div>
	)
}

function deadlineCell(wo: WorkOrder, workflows: BoardWorkflow[], now: number): TableBodyCell {
	if (!wo.dueAt) return { type: 'content', label: MISSING }
	const atRisk =
		!isTerminalStatus(wo, workflows) && new Date(wo.dueAt).getTime() - now <= DEADLINE_AT_RISK_DAYS * DAY_MS
	return { type: 'deadline', label: formatDayMonthYear(wo.dueAt), overdue: atRisk }
}

// The fixed columns sum to ~700px; this keeps ~240px for ID & Name.
const TABLE_MIN_WIDTH = 940

function columnsFor({
	workflows,
	userNames,
	brandLabels,
	onOpen,
	now,
}: Required<Pick<WorkOrderTableProps, 'workflows' | 'userNames' | 'brandLabels' | 'onOpen' | 'now'>>): TableColumn<
	WorkOrder & { id: string }
>[] {
	// Sortable columns are keyed by their sort key; widths as the Figma draws them.
	return [
		{
			id: WorkOrderSortKey.ORDER_CODE,
			title: 'Work Order ID & Name',
			allowsSorting: true,
			cell: (wo) => <IdName wo={wo} onOpen={onOpen} />,
		},
		{
			id: WorkOrderSortKey.BRAND,
			title: 'Brand',
			allowsSorting: true,
			className: 'w-27.5',
			cell: (wo) => ({ type: 'content', label: brandOf(wo, brandLabels) }),
		},
		{
			id: 'asset-type',
			title: 'Asset type',
			className: 'w-24.5',
			cell: (wo) => ({ type: 'content', label: assetTypeOf(wo), emphasis: true }),
		},
		{
			id: WorkOrderSortKey.STATUS,
			title: 'Status',
			allowsSorting: true,
			className: 'w-61.5',
			cell: (wo) => {
				const status = findBoardStatus(wo, workflows)
				return {
					type: 'status',
					label: status?.label ?? statusLabel(wo.status),
					tone: status ? PHASE_TONE[status.phase] : 'neutral',
				}
			},
		},
		{
			id: WorkOrderSortKey.ASSIGNEE,
			title: 'Designer',
			allowsSorting: true,
			className: 'w-28.75',
			cell: (wo) => designerCell(wo.assigneeIds ?? [], userNames),
		},
		{
			id: WorkOrderSortKey.DUE_AT,
			title: 'Deadline',
			allowsSorting: true,
			className: 'w-32.5',
			headerInset: 'narrow',
			cell: (wo) => deadlineCell(wo, workflows, now),
		},
	]
}

/**
 * The work-order list table. Presentational: sorting is requested through
 * `onSort` and performed by the caller (the server), and a row opens through
 * `onOpen` on press or Enter. Rows are child orders, so each carries a button
 * to its parent, which opens through `onOpen` too.
 */
export function WorkOrderTable({
	orders,
	workflows,
	userNames,
	brandLabels,
	sort,
	order,
	onSort,
	onOpen,
	now = Date.now(),
}: WorkOrderTableProps) {
	return (
		<Table
			aria-label="Work orders"
			minWidth={TABLE_MIN_WIDTH}
			columns={columnsFor({ workflows, userNames, brandLabels, onOpen, now })}
			rows={orders.map((wo) => ({ ...wo, id: wo.workOrderId }))}
			sortDescriptor={
				sort ? { column: sort, direction: order === SortDirection.ASC ? 'ascending' : 'descending' } : undefined
			}
			onSortChange={({ column }) => onSort(column as WorkOrderSortKey)}
			onRowAction={(id) => onOpen(String(id))}
		/>
	)
}
